import { useEffect, useMemo, useRef } from 'react';
import { useLocalParticipant, useParticipants, useRoomContext } from '@livekit/components-react';
import { Participant, RoomEvent, Track } from 'livekit-client';
import type { RoomOptions } from 'livekit-client';
import { useRoom } from '../room/RoomProvider';
import { isActiveModelCameraParticipant } from './liveKitCameraProfiles';
import { logVideoDiagnostic } from './videoDiagnostics';

interface LiveKitDiagnosticsProps {
  roomOptions: RoomOptions;
  connectWithMedia: boolean;
}

type DiagnosticContext = Parameters<typeof logVideoDiagnostic>[3];

function summarizeParticipant(participant: Participant | null | undefined) {
  if (!participant) return null;

  return {
    sid: participant.sid || null,
    identity: participant.identity,
    name: participant.name || null,
    isLocal: participant.isLocal,
    isSpeaking: participant.isSpeaking,
    audioLevel: participant.audioLevel,
    connectionQuality: participant.connectionQuality,
    isCameraEnabled: participant.isCameraEnabled,
    isMicrophoneEnabled: participant.isMicrophoneEnabled,
    trackPublications: participant.getTrackPublications().map(summarizePublication),
  };
}

function summarizePublication(publication: any) {
  return {
    trackSid: publication?.trackSid ?? publication?.sid ?? null,
    trackName: publication?.trackName ?? null,
    source: publication?.source ?? null,
    kind: publication?.kind ?? null,
    isMuted: publication?.isMuted ?? null,
    isSubscribed: publication?.isSubscribed ?? null,
    subscriptionStatus: publication?.subscriptionStatus ?? null,
    permissionStatus: publication?.permissionStatus ?? null,
    videoQuality: publication?.videoQuality ?? null,
    track: summarizeTrack(publication?.track),
  };
}

function summarizeTrack(track: any) {
  if (!track) return null;

  const mediaStreamTrack = track.mediaStreamTrack as MediaStreamTrack | undefined;
  return {
    sid: track.sid ?? null,
    name: track.name ?? null,
    source: track.source ?? null,
    kind: track.kind ?? mediaStreamTrack?.kind ?? null,
    currentBitrate: track.currentBitrate ?? null,
    dimensions: track.dimensions ?? null,
    isMuted: track.isMuted ?? mediaStreamTrack?.muted ?? null,
    mediaStreamTrack: mediaStreamTrack
      ? {
          id: mediaStreamTrack.id,
          label: mediaStreamTrack.label || null,
          kind: mediaStreamTrack.kind,
          enabled: mediaStreamTrack.enabled,
          muted: mediaStreamTrack.muted,
          readyState: mediaStreamTrack.readyState,
          contentHint: mediaStreamTrack.contentHint || null,
          settings: mediaStreamTrack.getSettings(),
        }
      : null,
  };
}

function summarizeRoomOptions(roomOptions: RoomOptions) {
  const publishDefaults = roomOptions.publishDefaults;
  const videoCaptureDefaults = roomOptions.videoCaptureDefaults;

  return {
    adaptiveStream: roomOptions.adaptiveStream ?? null,
    dynacast: roomOptions.dynacast ?? null,
    stopLocalTrackOnUnpublish: roomOptions.stopLocalTrackOnUnpublish ?? null,
    videoCaptureDefaults: videoCaptureDefaults
      ? {
          resolution: videoCaptureDefaults.resolution ?? null,
          facingMode: videoCaptureDefaults.facingMode ?? null,
        }
      : null,
    publishDefaults: publishDefaults
      ? {
          videoCodec: publishDefaults.videoCodec ?? null,
          simulcast: publishDefaults.simulcast ?? null,
          degradationPreference: publishDefaults.degradationPreference ?? null,
          videoEncoding: publishDefaults.videoEncoding ?? null,
        }
      : null,
  };
}

function summarizeSessionState(state: ReturnType<typeof useRoom>['sessionState']) {
  if (!state) return null;

  return {
    version: state.version,
    timestamp: state.timestamp,
    status: state.status,
    startedAt: state.startedAt,
    participantCount: state.participants.length,
    connectedParticipantCount: state.participants.filter((participant) => participant.isConnected).length,
    hostParticipantIds: state.hostParticipantIds,
    modelParticipantId: state.modelParticipantId,
    modelParticipantIds: state.modelParticipantIds,
    pinnedArtworkId: state.pinnedArtworkId,
    stagePinnedParticipantId: state.stagePinnedParticipantId,
    stageDisplayMode: state.stageDisplayMode,
    activeSpeakerTuning: state.activeSpeakerTuning,
    poseTimer: state.poseTimer
      ? {
          isActive: state.poseTimer.isActive,
          isPaused: state.poseTimer.isPaused,
          isBreak: state.poseTimer.isBreak,
          isUntimedBreak: state.poseTimer.isUntimedBreak,
          currentPose: state.poseTimer.currentPose,
          poseName: state.poseTimer.poseName,
          duration: state.poseTimer.duration,
          remaining: state.poseTimer.remaining,
          elapsed: state.poseTimer.elapsed,
        }
      : null,
    recording: state.recording,
    isHighResStreaming: state.isHighResStreaming,
  };
}

async function summarizeTrackStats(track: any) {
  const report = await track?.getRTCStatsReport?.();
  if (!report) return null;

  const summaries: Record<string, unknown>[] = [];

  report.forEach((stat: any) => {
    if (!['inbound-rtp', 'outbound-rtp', 'remote-inbound-rtp', 'candidate-pair'].includes(stat.type)) return;
    if (stat.type === 'candidate-pair' && !stat.selected && stat.nominated !== true) return;

    summaries.push({
      type: stat.type,
      kind: stat.kind ?? stat.mediaType ?? null,
      codecId: stat.codecId ?? null,
      transportId: stat.transportId ?? null,
      bytesSent: stat.bytesSent ?? null,
      bytesReceived: stat.bytesReceived ?? null,
      packetsSent: stat.packetsSent ?? null,
      packetsReceived: stat.packetsReceived ?? null,
      packetsLost: stat.packetsLost ?? null,
      jitter: stat.jitter ?? null,
      roundTripTime: stat.roundTripTime ?? stat.currentRoundTripTime ?? null,
      framesEncoded: stat.framesEncoded ?? null,
      framesDecoded: stat.framesDecoded ?? null,
      framesDropped: stat.framesDropped ?? null,
      frameWidth: stat.frameWidth ?? null,
      frameHeight: stat.frameHeight ?? null,
      framesPerSecond: stat.framesPerSecond ?? null,
      qualityLimitationReason: stat.qualityLimitationReason ?? null,
      qualityLimitationDurations: stat.qualityLimitationDurations ?? null,
      availableOutgoingBitrate: stat.availableOutgoingBitrate ?? null,
      availableIncomingBitrate: stat.availableIncomingBitrate ?? null,
    });
  });

  return summaries;
}

export function LiveKitDiagnostics({ roomOptions, connectWithMedia }: LiveKitDiagnosticsProps) {
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const liveKitParticipants = useParticipants();
  const {
    roomCode,
    sessionState,
    modelParticipantId,
    modelProfileId,
    modelParticipantIds,
    participants: sessionParticipants,
    isHost,
    poseTimer,
    recording,
  } = useRoom();
  const participantRole = sessionStorage.getItem(`participant-role-${roomCode}`) || null;
  const participantName = sessionStorage.getItem(`participant-name-${roomCode}`) || localParticipant?.name || null;
  const participantId = localParticipant?.identity || sessionStorage.getItem(`participant-id-${roomCode}`);
  const participantProfileId = useMemo(() => (
    participantId
      ? sessionParticipants.find(participant => participant.participantId === participantId)?.profileId ??
        sessionStorage.getItem(`participant-profileId-${roomCode}`)
      : null
  ), [participantId, roomCode, sessionParticipants]);
  const lastSessionStateSignatureRef = useRef<string | null>(null);

  const context: DiagnosticContext = useMemo(() => ({
    roomCode,
    participantId,
    participantName,
    participantRole,
  }), [participantId, participantName, participantRole, roomCode]);

  useEffect(() => {
    logVideoDiagnostic('info', 'diagnostics-mounted', {
      connectWithMedia,
      roomOptions: summarizeRoomOptions(roomOptions),
      roomState: {
        name: room.name,
        sid: (room as unknown as { sid?: string }).sid ?? null,
        state: room.state,
        numParticipants: room.numParticipants,
        canPlaybackAudio: room.canPlaybackAudio,
        canPlaybackVideo: room.canPlaybackVideo,
      },
      localParticipant: summarizeParticipant(localParticipant),
    }, context);

    return () => {
      logVideoDiagnostic('info', 'diagnostics-unmounted', {
        roomState: room.state,
        numParticipants: room.numParticipants,
      }, context);
    };
  }, []);

  useEffect(() => {
    logVideoDiagnostic('info', 'livekit-options-applied', {
      connectWithMedia,
      roomOptions: summarizeRoomOptions(roomOptions),
    }, context);
  }, [connectWithMedia, context, roomOptions]);

  useEffect(() => {
    const on = (event: RoomEvent, handler: (...args: any[]) => void) => {
      room.on(event as any, handler as any);
      return () => room.off(event as any, handler as any);
    };

    const cleanup = [
      on(RoomEvent.Connected, () => logVideoDiagnostic('info', 'livekit-connected', {
        roomState: room.state,
        sid: (room as unknown as { sid?: string }).sid ?? null,
        participantCount: room.numParticipants,
        localParticipant: summarizeParticipant(room.localParticipant),
      }, context)),
      on(RoomEvent.SignalConnected, () => logVideoDiagnostic('info', 'livekit-signal-connected', {
        roomState: room.state,
      }, context)),
      on(RoomEvent.ConnectionStateChanged, (state) => logVideoDiagnostic('info', 'livekit-connection-state-changed', {
        state,
        roomState: room.state,
      }, context)),
      on(RoomEvent.Reconnecting, () => logVideoDiagnostic('warn', 'livekit-reconnecting', {
        roomState: room.state,
      }, context)),
      on(RoomEvent.SignalReconnecting, () => logVideoDiagnostic('warn', 'livekit-signal-reconnecting', {
        roomState: room.state,
      }, context)),
      on(RoomEvent.Reconnected, () => logVideoDiagnostic('info', 'livekit-reconnected', {
        roomState: room.state,
      }, context)),
      on(RoomEvent.Disconnected, (reason) => logVideoDiagnostic('warn', 'livekit-disconnected', {
        reason,
        roomState: room.state,
      }, context)),
      on(RoomEvent.ParticipantConnected, (participant) => logVideoDiagnostic('info', 'participant-connected', {
        participant: summarizeParticipant(participant),
        participantCount: room.numParticipants,
      }, context)),
      on(RoomEvent.ParticipantDisconnected, (participant) => logVideoDiagnostic('warn', 'participant-disconnected', {
        participant: summarizeParticipant(participant),
        participantCount: room.numParticipants,
      }, context)),
      on(RoomEvent.TrackPublished, (publication, participant) => logVideoDiagnostic('info', 'remote-track-published', {
        participant: summarizeParticipant(participant),
        publication: summarizePublication(publication),
      }, context)),
      on(RoomEvent.TrackSubscribed, (track, publication, participant) => logVideoDiagnostic('info', 'remote-track-subscribed', {
        participant: summarizeParticipant(participant),
        publication: summarizePublication(publication),
        track: summarizeTrack(track),
      }, context)),
      on(RoomEvent.TrackSubscriptionFailed, (trackSid, participant) => logVideoDiagnostic('error', 'remote-track-subscription-failed', {
        trackSid,
        participant: summarizeParticipant(participant),
      }, context)),
      on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => logVideoDiagnostic('warn', 'remote-track-unsubscribed', {
        participant: summarizeParticipant(participant),
        publication: summarizePublication(publication),
        track: summarizeTrack(track),
      }, context)),
      on(RoomEvent.TrackMuted, (publication, participant) => logVideoDiagnostic('info', 'track-muted', {
        participant: summarizeParticipant(participant),
        publication: summarizePublication(publication),
      }, context)),
      on(RoomEvent.TrackUnmuted, (publication, participant) => logVideoDiagnostic('info', 'track-unmuted', {
        participant: summarizeParticipant(participant),
        publication: summarizePublication(publication),
      }, context)),
      on(RoomEvent.LocalTrackPublished, (publication, participant) => logVideoDiagnostic('info', 'local-track-published', {
        participant: summarizeParticipant(participant),
        publication: summarizePublication(publication),
      }, context)),
      on(RoomEvent.LocalTrackUnpublished, (publication, participant) => logVideoDiagnostic('warn', 'local-track-unpublished', {
        participant: summarizeParticipant(participant),
        publication: summarizePublication(publication),
      }, context)),
      on(RoomEvent.LocalAudioSilenceDetected, () => logVideoDiagnostic('warn', 'local-audio-silence-detected', {
        microphonePublication: summarizePublication(room.localParticipant.getTrackPublication(Track.Source.Microphone)),
      }, context)),
      on(RoomEvent.ConnectionQualityChanged, (quality, participant) => logVideoDiagnostic(
        quality === 'poor' || quality === 'lost' ? 'warn' : 'info',
        'connection-quality-changed',
        {
          quality,
          participant: summarizeParticipant(participant),
        },
        context
      )),
      on(RoomEvent.TrackStreamStateChanged, (publication, streamState, participant) => logVideoDiagnostic('warn', 'track-stream-state-changed', {
        streamState,
        participant: summarizeParticipant(participant),
        publication: summarizePublication(publication),
      }, context)),
      on(RoomEvent.TrackSubscriptionStatusChanged, (publication, status, participant) => logVideoDiagnostic('info', 'track-subscription-status-changed', {
        status,
        participant: summarizeParticipant(participant),
        publication: summarizePublication(publication),
      }, context)),
      on(RoomEvent.AudioPlaybackStatusChanged, () => logVideoDiagnostic(room.canPlaybackAudio ? 'info' : 'warn', 'audio-playback-status-changed', {
        canPlaybackAudio: room.canPlaybackAudio,
      }, context)),
      on(RoomEvent.VideoPlaybackStatusChanged, () => logVideoDiagnostic(room.canPlaybackVideo ? 'info' : 'warn', 'video-playback-status-changed', {
        canPlaybackVideo: room.canPlaybackVideo,
      }, context)),
      on(RoomEvent.MediaDevicesError, (error) => logVideoDiagnostic('error', 'media-devices-error', {
        error,
        lastCameraError: room.localParticipant.lastCameraError ?? null,
        lastMicrophoneError: room.localParticipant.lastMicrophoneError ?? null,
      }, context)),
      on(RoomEvent.MediaDevicesChanged, () => logVideoDiagnostic('info', 'media-devices-changed', null, context)),
      on(RoomEvent.ActiveDeviceChanged, (kind, deviceId) => logVideoDiagnostic('info', 'active-device-changed', {
        kind,
        deviceId,
      }, context)),
      on(RoomEvent.RecordingStatusChanged, (isRecording) => logVideoDiagnostic('info', 'livekit-recording-status-changed', {
        isRecording,
      }, context)),
    ];

    return () => {
      cleanup.forEach((unsubscribe) => unsubscribe());
    };
  }, [context, room]);

  useEffect(() => {
    if (!sessionState) return;
    const signature = JSON.stringify({
      status: sessionState.status,
      participantCount: sessionState.participants.length,
      connectedParticipantCount: sessionState.participants.filter((participant) => participant.isConnected).length,
      hostParticipantIds: sessionState.hostParticipantIds,
      modelParticipantId: sessionState.modelParticipantId,
      modelProfileId: sessionState.modelProfileId,
      modelParticipantIds: sessionState.modelParticipantIds,
      pinnedArtworkId: sessionState.pinnedArtworkId,
      stagePinnedParticipantId: sessionState.stagePinnedParticipantId,
      stageDisplayMode: sessionState.stageDisplayMode,
      activeSpeakerTuning: sessionState.activeSpeakerTuning,
      recording: sessionState.recording,
      poseTimer: sessionState.poseTimer
        ? {
            isActive: sessionState.poseTimer.isActive,
            isPaused: sessionState.poseTimer.isPaused,
            isBreak: sessionState.poseTimer.isBreak,
            isUntimedBreak: sessionState.poseTimer.isUntimedBreak,
            currentPose: sessionState.poseTimer.currentPose,
            poseName: sessionState.poseTimer.poseName,
          }
        : null,
    });
    if (lastSessionStateSignatureRef.current === signature) return;
    lastSessionStateSignatureRef.current = signature;

    logVideoDiagnostic('info', 'session-state-received', summarizeSessionState(sessionState), context);
  }, [context, sessionState]);

  useEffect(() => {
    logVideoDiagnostic('info', 'video-role-state-changed', {
      isHost,
      participantId,
      participantRole,
      modelParticipantId,
      modelProfileId,
      modelParticipantIds,
      participantProfileId,
      amSelectedModelCamera: isActiveModelCameraParticipant({
        participantId,
        participantProfileId,
        modelParticipantId,
        modelProfileId,
        modelParticipantIds,
      }),
      liveKitParticipantCount: liveKitParticipants.length,
      poseTimer: poseTimer
        ? {
            isActive: poseTimer.isActive,
            isPaused: poseTimer.isPaused,
            isBreak: poseTimer.isBreak,
            isUntimedBreak: poseTimer.isUntimedBreak,
            currentPose: poseTimer.currentPose,
            poseName: poseTimer.poseName,
          }
        : null,
      recording,
    }, context);
  }, [
    context,
    isHost,
    liveKitParticipants.length,
    modelParticipantId,
    modelParticipantIds,
    modelProfileId,
    participantId,
    participantProfileId,
    participantRole,
    poseTimer?.currentPose,
    poseTimer?.isActive,
    poseTimer?.isBreak,
    poseTimer?.isPaused,
    poseTimer?.isUntimedBreak,
    poseTimer?.poseName,
    recording,
  ]);

  useEffect(() => {
    const collectStats = async () => {
      const localCameraPublication = room.localParticipant.getTrackPublication(Track.Source.Camera);
      const localMicrophonePublication = room.localParticipant.getTrackPublication(Track.Source.Microphone);
      const primaryModelParticipantId = modelParticipantId ?? modelParticipantIds[0];
      const modelParticipant = primaryModelParticipantId
        ? liveKitParticipants.find((participant) => participant.identity === primaryModelParticipantId)
        : null;
      const modelCameraPublication = modelParticipant?.getTrackPublication(Track.Source.Camera);
      const modelMicrophonePublication = modelParticipant?.getTrackPublication(Track.Source.Microphone);

      try {
        logVideoDiagnostic('info', 'livekit-media-stats-snapshot', {
          roomState: room.state,
          canPlaybackAudio: room.canPlaybackAudio,
          canPlaybackVideo: room.canPlaybackVideo,
          participantCount: room.numParticipants,
          localParticipant: summarizeParticipant(room.localParticipant),
          modelParticipant: summarizeParticipant(modelParticipant),
          publications: {
            localCamera: summarizePublication(localCameraPublication),
            localMicrophone: summarizePublication(localMicrophonePublication),
            modelCamera: summarizePublication(modelCameraPublication),
            modelMicrophone: summarizePublication(modelMicrophonePublication),
          },
          stats: {
            localCamera: await summarizeTrackStats(localCameraPublication?.track),
            localMicrophone: await summarizeTrackStats(localMicrophonePublication?.track),
            modelCamera: await summarizeTrackStats(modelCameraPublication?.track),
            modelMicrophone: await summarizeTrackStats(modelMicrophonePublication?.track),
          },
        }, context);
      } catch (error) {
        logVideoDiagnostic('warn', 'livekit-media-stats-snapshot-failed', { error }, context);
      }
    };

    void collectStats();
    const interval = window.setInterval(collectStats, 20_000);
    return () => window.clearInterval(interval);
  }, [context, liveKitParticipants, modelParticipantId, modelParticipantIds, room]);

  return null;
}
